import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Tooltip from "@mui/material/Tooltip";
import {
  pixelToCameraCoord,
  cameraCoordToPixel,
  DEFAULT_FOCUS_RECT,
} from "../../helpers/focusUtils";

interface FocusRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface FocusRectOverlayProps {
  rect: FocusRect;
  onChange: (rect: FocusRect) => void;
  disabled?: boolean;
  visible?: boolean;
}

const FocusRectOverlay: React.FC<FocusRectOverlayProps> = ({
  rect,
  onChange,
  disabled = false,
  visible = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const el = containerRef.current?.parentElement;

    if (!el) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);

        setContainerHeight(entry.contentRect.height);
      }
    });

    observer.observe(el);

    setContainerWidth(el.clientWidth);

    setContainerHeight(el.clientHeight);

    return () => observer.disconnect();
  }, []);

  const [drawing, setDrawing] = useState(false);

  const [startPoint, setStartPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [currentPoint, setCurrentPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const getRelativePosition = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!containerRef.current) return { x: 0, y: 0 };

      const bounds = containerRef.current.getBoundingClientRect();

      const x = Math.max(0, Math.min(e.clientX - bounds.left, containerWidth));

      const y = Math.max(0, Math.min(e.clientY - bounds.top, containerHeight));

      return { x, y };
    },
    [containerWidth, containerHeight]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;

      e.preventDefault();

      const pos = getRelativePosition(e);

      setStartPoint(pos);

      setCurrentPoint(pos);

      setDrawing(true);

      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [disabled, getRelativePosition]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!drawing || disabled) return;

      e.preventDefault();

      setCurrentPoint(getRelativePosition(e));
    },
    [drawing, disabled, getRelativePosition]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!drawing || !startPoint || !currentPoint || disabled) return;

      e.preventDefault();

      setDrawing(false);

      const finalPoint = getRelativePosition(e);

      const x1 = Math.min(startPoint.x, finalPoint.x);

      const y1 = Math.min(startPoint.y, finalPoint.y);

      const x2 = Math.max(startPoint.x, finalPoint.x);

      const y2 = Math.max(startPoint.y, finalPoint.y);

      // Only update if the drawn rect has a minimum size (at least 10px)
      if (x2 - x1 > 10 && y2 - y1 > 10) {
        onChange({
          x1: pixelToCameraCoord(x1, containerWidth),
          y1: pixelToCameraCoord(y1, containerHeight),
          x2: pixelToCameraCoord(x2, containerWidth),
          y2: pixelToCameraCoord(y2, containerHeight),
        });
      }

      setStartPoint(null);

      setCurrentPoint(null);
    },
    [
      drawing,
      startPoint,
      currentPoint,
      disabled,
      getRelativePosition,
      containerWidth,
      containerHeight,
      onChange,
    ]
  );

  const handleReset = useCallback(() => {
    onChange(DEFAULT_FOCUS_RECT);
  }, [onChange]);

  if (!visible) return null;

  // Calculate display rect (either from drawing in progress or from saved rect)
  let displayLeft: number;

  let displayTop: number;

  let displayWidth: number;

  let displayHeight: number;

  if (drawing && startPoint && currentPoint) {
    displayLeft = Math.min(startPoint.x, currentPoint.x);

    displayTop = Math.min(startPoint.y, currentPoint.y);

    displayWidth = Math.abs(currentPoint.x - startPoint.x);

    displayHeight = Math.abs(currentPoint.y - startPoint.y);
  } else {
    displayLeft = cameraCoordToPixel(rect.x1, containerWidth);

    displayTop = cameraCoordToPixel(rect.y1, containerHeight);

    displayWidth = cameraCoordToPixel(rect.x2, containerWidth) - displayLeft;

    displayHeight = cameraCoordToPixel(rect.y2, containerHeight) - displayTop;
  }

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        cursor: disabled ? "default" : "crosshair",
        touchAction: "none",
        zIndex: 10,
      }}
    >
      {/* Focus rectangle */}
      <Box
        sx={{
          position: "absolute",
          left: displayLeft,
          top: displayTop,
          width: displayWidth,
          height: displayHeight,
          backgroundColor: "rgba(33, 150, 243, 0.3)",
          border: "2px solid #2196F3",
          pointerEvents: "none",
          boxSizing: "border-box",
        }}
      />

      {/* Reset button */}
      {!disabled && (
        <Tooltip title="Reset focus rectangle">
          <IconButton
            onClick={e => {
              e.stopPropagation();

              handleReset();
            }}
            onPointerDown={e => e.stopPropagation()}
            size="small"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
              zIndex: 11,
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default FocusRectOverlay;
